import { rectSortingStrategy, SortableContext } from '@dnd-kit/sortable';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Fragment, memo, useCallback, useContext, useMemo } from 'react';
import {
  type SwimlaneEventListener,
  useSwimlaneInternalEventHandler,
} from '@/components/ui/swimlane/api';
import {
  CardVirtualizationContext,
  useSwimlaneRootProps,
  useSwimlaneSlot,
  useSwimlaneState,
} from '@/components/ui/swimlane/context';
import type { IHeader, TColumnID, TRowID } from '@/components/ui/swimlane/types';
import { buildCellId, parseCardId } from '@/components/ui/swimlane/utils';
import { DRAFT_CARD_HEIGHT, GRID_GAP } from '../../constants';
import { useMemoizedValue } from '../../hooks/useMemoizedValue';
import { AddCardForm } from '../create/AddCardForm';
import { InlineAdd } from '../create/InlineAdd';
import { SortableCard } from './SortableCard';

type TCardsProps = {
  cellTop: number;
  columnHeader: IHeader;
  rowHeader?: IHeader;
};

const emptyCellStyle = { minWidth: 'inherit', minHeight: 'inherit' };

export const Cards = memo(({ cellTop, columnHeader, rowHeader, ...rest }: TCardsProps) => {
  const columnID: TColumnID = columnHeader.id;
  const rowID: TRowID = rowHeader?.id ?? 'null';
  const { readOnly, shouldHideInlineAdd, maxItemsPerCell } = useSwimlaneRootProps();
  const { component: SlotAddCardForm = AddCardForm, props: addCardFormSlotProps } =
    useSwimlaneSlot('addCardForm');
  const { getCellCardIDs, hasCardDraft, removeCardDraft, getCardHeight } = useSwimlaneState();
  const cellId = buildCellId({ rowID, columnID });
  const cardIDs = useMemoizedValue(getCellCardIDs(cellId));
  const cardIndexByID = useMemo(
    () => new Map(cardIDs.map((cardId, index) => [cardId, index])),
    [cardIDs],
  );
  const hideInlineAdd = shouldHideInlineAdd || cardIDs.length >= maxItemsPerCell;
  const rowVirtualizer = useContext(CardVirtualizationContext);

  const getCardId = useCallback((index: number) => cardIDs[index] ?? '', [cardIDs]);

  const calculateVirtualItemHeight = useCallback(
    (index: number, cardHeight: number) => {
      const cardId = getCardId(index);
      const cellDraftHeight =
        !readOnly && index === 0 && hasCardDraft(cellId)
          ? DRAFT_CARD_HEIGHT.value + GRID_GAP.value
          : 0;
      const cardDraftHeight =
        !readOnly && hasCardDraft(cardId) ? DRAFT_CARD_HEIGHT.value + GRID_GAP.value : 0;

      return cellDraftHeight + cardHeight + cardDraftHeight;
    },
    [cellId, getCardId, hasCardDraft, readOnly],
  );

  const estimateSize = useCallback(
    (index: number) => {
      const cardId = getCardId(index);
      const cardHeight = getCardHeight(cardId);

      return calculateVirtualItemHeight(index, cardHeight);
    },
    [calculateVirtualItemHeight, getCardHeight, getCardId],
  );

  const cardsVirtualizer = useVirtualizer({
    count: cardIDs.length,
    getScrollElement: () => rowVirtualizer?.scrollElement ?? null,
    getItemKey: getCardId,
    estimateSize,
    gap: GRID_GAP.value,
    overscan: 2,
    scrollMargin: cellTop,
    initialOffset: rowVirtualizer?.scrollOffset ?? 0,
  });

  const onCardHeightChange = useCallback<SwimlaneEventListener<'swimlaneCardHeightChange'>>(
    ({ changedCards }) => {
      if (changedCards.get(cellId)?.size) {
        for (const [cardId, cardHeight] of changedCards.get(cellId) || new Map()) {
          const index = cardIndexByID.get(cardId);
          if (typeof index === 'number') {
            cardsVirtualizer.resizeItem(index, calculateVirtualItemHeight(index, cardHeight));
          }
        }
      }
    },
    [calculateVirtualItemHeight, cardIndexByID, cardsVirtualizer, cellId],
  );
  useSwimlaneInternalEventHandler('swimlaneCardHeightChange', onCardHeightChange);

  const virtualCards = cardsVirtualizer.getVirtualItems();

  return (
    <SortableContext items={cardIDs} strategy={rectSortingStrategy}>
      <div
        className="flex flex-col items-center"
        data-testid="virtual-cards"
        style={
          !virtualCards.length
            ? emptyCellStyle
            : {
                rowGap: GRID_GAP.label,
                transform: `translateY(${(virtualCards[0]?.start ?? 0) - cardsVirtualizer.options.scrollMargin}px)`,
              }
        }
      >
        {!readOnly &&
          !cardIDs.length &&
          !virtualCards.length &&
          (hasCardDraft(cellId) ? (
            <SlotAddCardForm
              key={`card-draft-${cellId}`}
              columnHeader={columnHeader}
              rowHeader={rowHeader}
              index={0}
              onClose={() => removeCardDraft(cellId)}
              {...addCardFormSlotProps}
            />
          ) : (
            !hideInlineAdd && <InlineAdd rowID={rowID} columnID={columnID} variant="card" />
          ))}
        {virtualCards.map(({ index }) => {
          const cardId = getCardId(index);
          const { itemID } = parseCardId(cardId);
          return (
            <Fragment key={cardId}>
              {!readOnly && index === 0 && hasCardDraft(cellId) && (
                <SlotAddCardForm
                  key={`card-draft-${cellId}`}
                  columnHeader={columnHeader}
                  rowHeader={rowHeader}
                  index={0}
                  onClose={() => removeCardDraft(cellId)}
                  {...addCardFormSlotProps}
                />
              )}
              <SortableCard cardId={cardId} {...rest} />
              {!readOnly &&
                (hasCardDraft(cardId) ? (
                  <SlotAddCardForm
                    key={`card-draft-${cardId}`}
                    columnHeader={columnHeader}
                    rowHeader={rowHeader}
                    index={index}
                    onClose={() => removeCardDraft(cardId)}
                    {...addCardFormSlotProps}
                  />
                ) : (
                  !hideInlineAdd && (
                    <InlineAdd
                      rowID={rowID}
                      columnID={columnID}
                      prevItemID={itemID}
                      variant="divider"
                    />
                  )
                ))}
            </Fragment>
          );
        })}
      </div>
    </SortableContext>
  );
});
